import { createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "dayjs";

import { type ProgramWorkoutWithMetadata } from "@fitness-app/data-models/domain/services/WorkoutService";
import {
  ActivityEventType,
  type WorkoutActivityEvent,
  type WorkoutActivityEventWithFeedback,
} from "@fitness-app/data-models/entities/ProgramActivity";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { type AsyncThunkCreator } from "../../../index";
import { addActivity } from "../../traineeActivities/reducer";
import { LIVE_WORKOUT_REDUCER_NAME } from "../types";

type Payload = {
  traineeId: string;
  workout: ProgramWorkoutWithMetadata;
  eventDate: string;
};

export const getActivityIdForLiveWorkoutStart = ({
  requestedWorkout,
  currentWorkout,
  editedDocId,
}: {
  requestedWorkout: ProgramWorkoutWithMetadata;
  currentWorkout?: { id: string } | null;
  editedDocId?: string | null;
}) => requestedWorkout.activityId ?? (currentWorkout?.id === requestedWorkout.id ? editedDocId : null);

const ACTIVITY_WITH_RATE =
  "*, workoutRate:feedbackId(data->activityId, data->workoutId, data->rate, data->comment, data->trainingStatus, data->programId)";

export const fetchSelectedActivity = createAsyncThunk<WorkoutActivityEvent, Payload, AsyncThunkCreator<string>>(
  `${LIVE_WORKOUT_REDUCER_NAME}/fetchSelectedActivity`,
  async (payload, { rejectWithValue, getState, dispatch, extra: { db, auth } }) => {
    const { currentWorkout, editedDocId } = getState().traineeActivities.data;
    const { selectedProgram } = getState().traineeProgram;
    const workout = currentWorkout?.id === payload.workout.id ? currentWorkout : payload.workout;
    const activityId = getActivityIdForLiveWorkoutStart({
      requestedWorkout: payload.workout,
      currentWorkout,
      editedDocId,
    });

    const user = await getLoggedUser(auth);

    if (activityId) {
      const { error, data } = await db
        .from("client_workout_activity")
        .select(ACTIVITY_WITH_RATE)
        .order("createdAt", { ascending: false })
        .eq("traineeId", payload.traineeId)
        .eq("id", activityId)
        .maybeSingle<WorkoutActivityEventWithFeedback>();

      if (error || !data) {
        return rejectWithValue(error?.message || "Activity not found");
      }

      return data;
    }

    const date = dayjs(payload.eventDate, "YYYY-MM-DD").hour(12);
    const eventDate = date.format("YYYY-MM-DD");
    const programId =
      selectedProgram && date.isSameOrAfter(selectedProgram.startDate, "day") ? selectedProgram.id : null;

    // The caller may still hold the workout without the activity an earlier start
    // of the same day created (interrupting a workout and starting it again).
    // Inserting again breaks the unique (traineeId, workoutId, eventDate,
    // programId) key and the start fails silently, so reuse that day's activity.
    const existingQuery = db
      .from("client_workout_activity")
      .select(ACTIVITY_WITH_RATE)
      .eq("traineeId", payload.traineeId)
      .eq("workoutId", workout.id)
      .eq("eventDate", eventDate);
    const { data: existing, error: existingError } = await (
      programId ? existingQuery.eq("programId", programId) : existingQuery.is("programId", null)
    ).maybeSingle<WorkoutActivityEventWithFeedback>();

    if (existingError) {
      return rejectWithValue(existingError.message);
    }

    if (existing) {
      return existing;
    }

    let event: WorkoutActivityEvent = {
      id: generateUniqId(),
      type: ActivityEventType.Workout,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      eventDate,
      eventMonth: date.format("YYYY-MM"),
      eventWeek: `${date.format("YYYY")}-${date.isoWeek()}`,
      programId,
      traineeId: payload.traineeId,
      clientEmail: user.email,
      userId: user.id,
      eventTimestamp: date.toISOString(),
      workoutId: workout.id,
      data: {
        ...workout,
        replicatedFromSchedule: true,
        updatedAt: new Date().toISOString(),
      },
      feedbackId: null,
      metadata: null,
      synced: false,
    };

    const { error, data } = await db
      .from("client_workout_activity")
      .insert(event)
      .select("*")
      .single<WorkoutActivityEvent>();

    if (data) {
      event = data;
    }

    if (error) {
      return rejectWithValue(error.message);
    }

    dispatch(addActivity(event));

    return event;
  },
);
